import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useLazyGetTimedActionsQuery,
  usePostTimedActionMutation,
  usePutTimedActionMutation,
  useDeleteTimedActionMutation,
} from "../../redux/timedActions/timedActionsAPI";
import GETJwtToken from "../../redux/authentication/authentication";
import {
  TimedActionDraft,
  mapTimedActionTToDraft,
  emptyTimedActionDraft,
} from "./scheduledActionTypes";
import { TimedActionT } from "../../flatbuffers/generated/archive/timed-action";
import TimedActionAlerts from "./TimedActionAlerts";

// ── Scope ─────────────────────────────────────────────────────────────────────

/**
 * Which set of timed actions a container operates on. The `kind` discriminant
 * selects the fetch strategy and the seed used when creating a new action:
 *  - `organization` — wizard: all actions for one org (fetched by organization_id)
 *  - `bank`         — control panel bank: actions for one bank (fetched by bank_id)
 *  - `lift`         — control panel lift: the bank's actions (fetched by bank_id),
 *                     which the container then splits into lift-specific vs bank-wide
 *  - `orgs`         — monitoring drawer: actions merged across one or more orgs
 */
export type TimedActionsScope =
  | { kind: "organization"; organizationId: string }
  | { kind: "bank"; organizationId: string; bankId: string }
  | { kind: "lift"; organizationId: string; bankId: string; liftId: string }
  | { kind: "orgs"; organizationId: string; organizationIds?: string[] };

export interface UseTimedActionsCrudOptions {
  /** When false, the fetch effect is suppressed (default true). Used to gate
   *  fetching behind a permission check or a closed drawer. */
  enabled?: boolean;
}

export interface UseTimedActionsCrudResult {
  actions: TimedActionDraft[];
  isFetching: boolean;
  fetchError: unknown;
  submitting: boolean;
  modalOpen: boolean;
  editDraft: TimedActionDraft | null;
  /** Stable callback that closes the edit modal — referentially constant across
   *  renders so consumers can pass it straight to onCancel without re-allocating.
   *  Opening is owned internally by handleCreate/handleEdit (which seed a draft). */
  closeModal: () => void;
  fetchActions: () => Promise<void>;
  handleCreate: (overrides?: Partial<TimedActionDraft>) => void;
  handleEdit: (action: TimedActionDraft) => void;
  handleSubmit: (draft: TimedActionDraft) => Promise<void>;
  handleDelete: (action: TimedActionDraft) => Promise<void>;
  handleToggleEnabled: (action: TimedActionDraft, enabled: boolean) => Promise<void>;
  alerts: React.ReactElement;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Extract the TimedActionT[] payload from an RTK query result and map to drafts. */
function resultToDrafts(result: unknown): TimedActionDraft[] {
  const raw: TimedActionT[] = (result as { data?: TimedActionT[] }).data ?? [];
  return raw.map(mapTimedActionTToDraft);
}

/**
 * Upsert a draft into a drafts list by id — replaces a matching id in place,
 * otherwise appends. A draft with no id is a no-op (nothing to key on).
 */
function upsertDraft(drafts: TimedActionDraft[], saved: TimedActionDraft): TimedActionDraft[] {
  if (saved.id === undefined) return drafts;
  const idx = drafts.findIndex((d) => d.id === saved.id);
  if (idx === -1) return [...drafts, saved];
  const next = drafts.slice();
  next[idx] = saved;
  return next;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Owns every piece of CRUD wiring the four scheduled-action containers used to
 * duplicate: the four RTK hooks, the `actions` list, `fetchActions`, the create/
 * edit/submit/delete/toggle handlers, and the `alerts` slab. Containers keep only
 * their own chrome (selectors, permission gates, drawer/modal shells, layout).
 */
export function useTimedActionsCrud(
  scope: TimedActionsScope,
  options: UseTimedActionsCrudOptions = {},
): UseTimedActionsCrudResult {
  const enabled = options.enabled ?? true;

  // Extract primitive scope fields up front so the callbacks below depend on
  // stable scalars instead of the (freshly-allocated each render) scope object —
  // otherwise the mount effect would re-run every render and refetch forever.
  const scopeKind = scope.kind;
  const { organizationId } = scope;
  const bankId = scope.kind === "bank" || scope.kind === "lift" ? scope.bankId : undefined;
  const liftId = scope.kind === "lift" ? scope.liftId : undefined;
  // Serialize the org id list to a stable string so the deps array compares by
  // value rather than by the array's identity (UUIDs never contain commas).
  const orgIdsKey =
    scope.kind === "orgs"
      ? (scope.organizationIds && scope.organizationIds.length > 0
          ? scope.organizationIds
          : [scope.organizationId]
        ).join(",")
      : "";

  const [triggerGetTimedActions, { error: fetchError }] = useLazyGetTimedActionsQuery();
  const [
    postTimedAction,
    { isLoading: isPosting, isSuccess: postOk, isError: postErr, error: postError },
  ] = usePostTimedActionMutation();
  const [
    putTimedAction,
    { isLoading: isPutting, isSuccess: putOk, isError: putErr, error: putError },
  ] = usePutTimedActionMutation();
  const [deleteTimedAction, { isSuccess: deleteOk, isError: deleteErr, error: deleteError }] =
    useDeleteTimedActionMutation();

  const [actions, setActions] = useState<TimedActionDraft[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editDraft, setEditDraft] = useState<TimedActionDraft | null>(null);
  // Local loading flag + sequence guard. RTK's isFetching follows only the
  // last-triggered query, so it is unreliable for the multi-org Promise.all
  // fan-out; and a rapid edit/toggle/delete can run two fetchActions
  // concurrently, where a slow earlier response would otherwise clobber the
  // list with stale rows. Only the most recent call commits its result.
  const [loading, setLoading] = useState(false);
  const fetchSeqRef = useRef(0);

  const fetchActions = useCallback(async () => {
    if (!enabled) return;
    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;
    const isLatest = () => seq === fetchSeqRef.current;
    const commit = (drafts: TimedActionDraft[]) => {
      if (isLatest()) setActions(drafts);
    };
    setLoading(true);
    try {
      const token = await GETJwtToken();
      if (scopeKind === "bank" || scopeKind === "lift") {
        if (!bankId) return;
        const result = await triggerGetTimedActions({ bankId, token }).unwrap();
        commit(resultToDrafts(result));
      } else if (scopeKind === "organization") {
        if (!organizationId) return;
        const result = await triggerGetTimedActions({ organizationId, token }).unwrap();
        commit(resultToDrafts(result));
      } else {
        const orgIds = orgIdsKey ? orgIdsKey.split(",") : [];
        if (orgIds.length === 0) return;
        const results = await Promise.all(
          orgIds.map((id) => triggerGetTimedActions({ organizationId: id, token }).unwrap()),
        );
        commit(results.flatMap(resultToDrafts));
      }
    } catch (err) {
      // The rejected-request case is surfaced separately via `fetchError`
      // (the query hook's own error state). This catch also covers a THROW
      // during response handling (e.g. resultToDrafts/mapTimedActionTToDraft
      // choking on a malformed row) — that has no `fetchError` counterpart,
      // so without logging it here a broken refresh vanishes with zero trace:
      // the list just silently stays stale.
      // eslint-disable-next-line no-console
      console.error("[useTimedActionsCrud] fetchActions failed:", err);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [enabled, scopeKind, bankId, organizationId, orgIdsKey, triggerGetTimedActions]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const createSeed = useMemo<
    Partial<TimedActionDraft> & { bank_id: string; organization_id: string }
  >(() => {
    if (scopeKind === "lift") {
      return { bank_id: bankId ?? "", organization_id: organizationId, lift_id: liftId };
    }
    if (scopeKind === "bank") {
      return { bank_id: bankId ?? "", organization_id: organizationId };
    }
    // organization scope: the wizard overrides bank_id via handleCreate(...).
    // orgs scope never creates (the drawer is delete-only), so this seed is moot.
    return { bank_id: "", organization_id: organizationId };
  }, [scopeKind, bankId, organizationId, liftId]);

  const handleCreate = useCallback(
    (overrides?: Partial<TimedActionDraft>) => {
      setEditDraft(emptyTimedActionDraft({ ...createSeed, ...overrides }));
      setModalOpen(true);
    },
    [createSeed],
  );

  const handleEdit = useCallback((action: TimedActionDraft) => {
    setEditDraft(action);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const handleSubmit = useCallback(
    async (draft: TimedActionDraft) => {
      try {
        const token = await GETJwtToken();
        const saved = draft.id
          ? await putTimedAction({ id: draft.id, body: draft, token }).unwrap()
          : await postTimedAction({ body: draft, token }).unwrap();
        // Show the saved row from the mutation's OWN response immediately,
        // rather than depending solely on the fetchActions() below. That
        // create/edit response IS the saved row, so the list has no reason to
        // wait on a second round trip for it — previously the modal closed on
        // a bare success toast and the list only updated if the follow-up GET
        // happened to return in time and decode cleanly, which is exactly the
        // kind of gap that leaves a just-created action invisible until the
        // next full page load.
        const savedDraft = mapTimedActionTToDraft(saved as TimedActionT);
        setActions((prev) => upsertDraft(prev, savedDraft));
        setModalOpen(false);
        await fetchActions();
        // The refetch above can legitimately race a slow/inconsistent
        // backend read and land without the row just saved — the exact GET
        // this upsert exists to stop depending on. Re-assert it so that
        // refetch can only ADD freshness, never erase what the user just did.
        setActions((prev) => upsertDraft(prev, savedDraft));
      } catch {
        // error shown via mutation state
      }
    },
    [putTimedAction, postTimedAction, fetchActions],
  );

  const handleDelete = useCallback(
    async (action: TimedActionDraft) => {
      if (!action.id) return;
      try {
        const token = await GETJwtToken();
        await deleteTimedAction({ id: action.id, token }).unwrap();
        await fetchActions();
      } catch {
        // error shown via mutation state
      }
    },
    [deleteTimedAction, fetchActions],
  );

  const handleToggleEnabled = useCallback(
    async (action: TimedActionDraft, enabledFlag: boolean) => {
      if (!action.id) return;
      try {
        const token = await GETJwtToken();
        await putTimedAction({
          id: action.id,
          body: { ...action, enabled: enabledFlag },
          token,
        }).unwrap();
        await fetchActions();
      } catch {
        // error shown via mutation state
      }
    },
    [putTimedAction, fetchActions],
  );

  const alerts = (
    <TimedActionAlerts
      postOk={postOk}
      postErr={postErr}
      postError={postError}
      putOk={putOk}
      putErr={putErr}
      putError={putError}
      deleteOk={deleteOk}
      deleteErr={deleteErr}
      deleteError={deleteError}
    />
  );

  return {
    actions,
    isFetching: loading,
    fetchError,
    submitting: isPosting || isPutting,
    modalOpen,
    editDraft,
    closeModal,
    fetchActions,
    handleCreate,
    handleEdit,
    handleSubmit,
    handleDelete,
    handleToggleEnabled,
    alerts,
  };
}

export default useTimedActionsCrud;
